import { useEffect, useMemo, useState } from "react"
import TreeAPI from "../../apis/TreeAPI";
import produce from "immer";
import { flatten } from "flat";
import { SubscribeToChaptersCompleted, SubscribeToEpisodesCompleted, SubscribeToLessonsCompleted } from "../../apis/User/indexv2";

const useCompleted = (props: IUseCompletedProps) => {

    const [episodesCompleted, setEpisodesCompleted] = useState<{
        [episodeId: string]: true | undefined;
    }>({});

    const [chaptersCompleted, setChaptersCompleted] = useState<{
        [chapterId: string]: true | undefined;
    }>({});

    const [lessonsCompleted, setLessonsCompleted] = useState<{
        [lessonId: string]: true | undefined;
    }>({});

    useEffect(() => {
        setEpisodesCompleted({});
        if (props.userId === undefined) return;
        const unsubscribe = SubscribeToEpisodesCompleted({
            userId: props.userId,
            onGet: (episodeId) => {
                setEpisodesCompleted(
                    produce(draft => {
                        draft[episodeId] = true;
                    })
                );
            },
            onDelete: (episodeId) => {
                setEpisodesCompleted(
                    produce(draft => {
                        draft[episodeId] = undefined;
                    })
                );
            }
        });

        return () => unsubscribe();
    }, [props.userId]);

    useEffect(() => {
        setChaptersCompleted({});
        if (props.userId === undefined || props.episodeId === undefined) return;
        const unsubscribe = SubscribeToChaptersCompleted({
            userId: props.userId,
            episodeId: props.episodeId,
            onGet: (chapterId) => {
                setChaptersCompleted(
                    produce(draft => {
                        draft[chapterId] = true;
                    })
                );
            },
            onDelete: (chapterId) => {
                setChaptersCompleted(
                    produce(draft => {
                        draft[chapterId] = undefined;
                    })
                );
            }
        });

        return () => unsubscribe();
    }, [props.userId, props.episodeId]);

    useEffect(() => {
        setLessonsCompleted({});
        if (props.userId === undefined || props.chapterId === undefined) return;
        const unsubscribe = SubscribeToLessonsCompleted({
            userId: props.userId,
            chapterId: props.chapterId,
            onGet: (lessonId) => {
                setLessonsCompleted(
                    produce(draft => {
                        draft[lessonId] = true;
                    })
                );
            },
            onDelete: (lessonId) => {
                setLessonsCompleted(
                    produce(draft => {
                        draft[lessonId] = undefined;
                    })
                );
            }
        });

        return () => unsubscribe();
    }, [props.userId, props.chapterId]);

    const completed = useMemo(() => {
        return {
            ...episodesCompleted,
            ...chaptersCompleted,
            ...lessonsCompleted
        };
    }, [lessonsCompleted, chaptersCompleted, episodesCompleted]);

    const completedArray = useMemo(() => {
        const keys = Object.keys(completed);

        const array: string[] = [];

        for (let index = 0; index < keys.length; index++) {
            const key = keys[index];
            array.push(key);
        }

        return array;
    }, [completed]);

    return { completed, completedArray };
}

export { useCompleted }